import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  listMarketingTemplatesByGoal,
  createMarketingTemplate,
  updateMarketingTemplate,
  deleteMarketingTemplate,
  duplicateMarketingTemplate,
  ensureDefaultEmailReminderTemplate,
} from "../../api/marketingCampaigns";
import SBINModal from "../../components/modals/SBINModal";
import ConfirmModal from "../../components/modals/ConfirmModal";
import InfoTooltip from "../../components/common/InfoTooltip";
import ModalCloseX from "../../components/common/ModalCloseX";
import ReportHelpCard from "../../components/reports/ReportHelpCard";
import { getCurrentSalonId, getCurrentSalonName } from "../../utils/currentSalon";
import { REPORT_HELP } from "../../constants/reportHelpContent";
import useHelpGuideNavigation from "../../hooks/useHelpGuideNavigation";
import SafeHtml from "../../components/common/SafeHtml";
import VisualTemplateBodyEditor, {
  sanitizeTemplateEditorHtml,
  templateHtmlToText,
} from "../../components/marketing/VisualTemplateBodyEditor";
import "../MarketingSectionCompact.css";

const REFILL_REMINDER_GOAL = "REFILL_REMINDER";
const FUTURE_APPOINTMENTS_GOAL = "FUTURE_APPOINTMENTS";
const DEFAULT_REFILL_TEMPLATE_NAME = "Nail Refill Reminder (Default)";
const DEFAULT_FUTURE_APPOINTMENTS_TEMPLATE_NAME = "Future Appointments (Default)";
const TEMPLATE_VARIABLES = [
  "{first_name}",
  "{salon_name}",
  "{salon_phone}",
  "{salon_address}",
  "{booking_url}",
];
const PREVIEW_FALLBACK_VALUES = {
  first_name: "Ava",
  salon_phone: "(555) 123-4567",
  salon_address: "123 Main St, Springfield, IL 62704",
  booking_url: "",
};

const FUTURE_APPOINTMENTS_PREVIEW_FALLBACK_VALUES = {
  first_name: "Ava",
  salon_phone: "(555) 123-4567",
  salon_address: "123 Main St, Springfield, IL 62704",
  portal_link: "https://ellyanna.com/portal/salon/13",
  future_appointments_html:
    "<table style=\"width:100%;border-collapse:collapse;margin:16px 0;font-size:14px;color:#1f2937;\"><thead><tr style=\"background:#f8fafc;\"><th style=\"padding:10px 12px;border:1px solid #cbd5e1;text-align:left;\">Service</th><th style=\"padding:10px 12px;border:1px solid #cbd5e1;text-align:left;\">When</th><th style=\"padding:10px 12px;border:1px solid #cbd5e1;text-align:left;\">With</th><th style=\"padding:10px 12px;border:1px solid #cbd5e1;text-align:left;\">Scheduled By</th></tr></thead><tbody><tr><td style=\"padding:10px 12px;border:1px solid #cbd5e1;\">Porcelain Refill</td><td style=\"padding:10px 12px;border:1px solid #cbd5e1;\">Tue, Apr 14, 2026 10:00 AM</td><td style=\"padding:10px 12px;border:1px solid #cbd5e1;\">Liliane P</td><td style=\"padding:10px 12px;border:1px solid #cbd5e1;\">Maria V</td></tr><tr><td style=\"padding:10px 12px;border:1px solid #cbd5e1;\">Pedicure - Gel Shellac</td><td style=\"padding:10px 12px;border:1px solid #cbd5e1;\">Mon, Apr 20, 2026 11:00 AM</td><td style=\"padding:10px 12px;border:1px solid #cbd5e1;\">Jenny X</td><td style=\"padding:10px 12px;border:1px solid #cbd5e1;\">Evano</td></tr></tbody></table>",
  future_appointments_text:
    "- Porcelain Refill | Tue, Apr 14, 2026 10:00 AM | With Liliane P | Scheduled by Maria V\n- Pedicure - Gel Shellac | Mon, Apr 20, 2026 11:00 AM | With Jenny X | Scheduled by Evano",
};

const FUTURE_APPOINTMENTS_TEMPLATE_VARIABLES = [
  "{first_name}",
  "{salon_name}",
  "{salon_phone}",
  "{salon_address}",
  "{future_appointments_html}",
  "{future_appointments_text}",
  "{portal_link}",
];

const TEMPLATE_GOAL_CONFIG = {
  [REFILL_REMINDER_GOAL]: {
    label: "Refill reminder",
    defaultTemplateName: DEFAULT_REFILL_TEMPLATE_NAME,
    createTemplateName: "Nail Refill Reminder",
    variables: TEMPLATE_VARIABLES,
    previewFallbackValues: PREVIEW_FALLBACK_VALUES,
    sampleLabel: "Sample data",
    bodyHtmlTooltip:
      "Use variables like {first_name} and {booking_url}. Keep it mobile-friendly.",
    clientSettings: [
      "Reminders only send to clients subscribed to Email Marketing.",
      'Refill reminders appear on client profiles after a service containing "full set" or "refill".',
      "Choose a reminder interval between 2 and 5 weeks.",
    ],
    troubleshooting: [
      "No emails sending: confirm client is subscribed, has an email, and has reminder enabled.",
      "Template not found: activate a refill reminder template.",
      "Booking link missing: add booking URL or include phone number fallback.",
      "Automation: run POST /system/jobs/run-refill-reminder-email-queue on a server cron schedule (for example, every 15 minutes).",
    ],
  },
  [FUTURE_APPOINTMENTS_GOAL]: {
    label: "Future appointments",
    defaultTemplateName: DEFAULT_FUTURE_APPOINTMENTS_TEMPLATE_NAME,
    createTemplateName: "Future Appointments",
    variables: FUTURE_APPOINTMENTS_TEMPLATE_VARIABLES,
    previewFallbackValues: FUTURE_APPOINTMENTS_PREVIEW_FALLBACK_VALUES,
    sampleLabel: "Sample appointments",
    bodyHtmlTooltip:
      "Use variables like {first_name}, {future_appointments_html}, and {portal_link}.",
    clientSettings: [
      "This template is used when staff email a client the full Future Appointments list from Client Profile.",
      "If the client has no email saved, the sender can enter one directly in the modal.",
      "The client email can be saved back to the client profile after the schedule email is sent.",
    ],
    troubleshooting: [
      "No emails sending: confirm the client has at least one future appointment and a valid email address.",
      "Template not found: activate a future appointments template.",
      "Missing appointment rows: verify the template body includes {future_appointments_html} or {future_appointments_text}.",
      "Portal link missing: use {portal_link} or a static CTA URL if you want a clickable follow-up link.",
    ],
  },
};

const DEFAULT_FORM = {
  name: "",
  subject: "",
  preheader: "",
  body_html: "",
  body_text: "",
  cta_label: "",
  cta_url: "",
  is_active: true,
};

const emptyToNull = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed ? trimmed : null;
};

const replaceTemplateTokens = (input, context) => {
  let output = String(input || "");
  Object.entries(context || {}).forEach(([key, value]) => {
    const replacement = String(value || "");
    const doubleBracePattern = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "gi");
    const singleBracePattern = new RegExp(`\\{${key}\\}`, "gi");
    output = output.replace(doubleBracePattern, replacement);
    output = output.replace(singleBracePattern, replacement);
  });
  return output;
};

export const buildSalonBookingPreviewUrl = (salonId) => {
  const rawSalonId = String(salonId || "").trim();
  if (!rawSalonId) return "";
  const configuredHost = String(process.env.REACT_APP_OPS_HOST || "ellyanna.com")
    .trim()
    .replace(/\/+$/, "");
  const baseUrl = /^https?:\/\//i.test(configuredHost)
    ? configuredHost
    : `https://${configuredHost}`;
  return `${baseUrl}/portal/salon/${encodeURIComponent(rawSalonId)}/book`;
};

export default function EmailReminders() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.EMAIL_REMINDERS;

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editing, setEditing] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(REFILL_REMINDER_GOAL);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [textFallbackEdited, setTextFallbackEdited] = useState(false);
  const goalConfig = TEMPLATE_GOAL_CONFIG[selectedGoal] || TEMPLATE_GOAL_CONFIG[REFILL_REMINDER_GOAL];
  const bookingPreviewUrl = useMemo(
    () => buildSalonBookingPreviewUrl(salonId),
    [salonId]
  );
  const previewContext = useMemo(
    () => ({
      ...(goalConfig?.previewFallbackValues || PREVIEW_FALLBACK_VALUES),
      salon_name: salonName || "Your Salon",
      booking_url: bookingPreviewUrl,
    }),
    [bookingPreviewUrl, goalConfig, salonName]
  );

  const sortedTemplates = useMemo(() => {
    return [...templates].sort((a, b) => {
      if (Boolean(a?.is_active) !== Boolean(b?.is_active)) {
        return a?.is_active ? -1 : 1;
      }
      const aDate = new Date(a?.updated_at || a?.created_at || 0).getTime();
      const bDate = new Date(b?.updated_at || b?.created_at || 0).getTime();
      return bDate - aDate;
    });
  }, [templates]);

  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return sortedTemplates[0] || null;
    return (
      sortedTemplates.find((template) => String(template.id) === String(selectedTemplateId)) ||
      sortedTemplates[0] ||
      null
    );
  }, [selectedTemplateId, sortedTemplates]);

  const getTemplatePreview = useCallback(
    (templateLike) => {
      const subjectPreview = replaceTemplateTokens(
        templateLike?.subject || "",
        previewContext
      );
      const preheaderPreview = replaceTemplateTokens(
        templateLike?.preheader || "",
        previewContext
      );
      const htmlPreview = replaceTemplateTokens(
        templateLike?.body_html || "",
        previewContext
      );
      const textPreview = replaceTemplateTokens(
        templateLike?.body_text || "",
        previewContext
      );
      return {
        subjectPreview,
        preheaderPreview,
        htmlPreview,
        textPreview,
      };
    },
    [previewContext]
  );

  const selectedTemplatePreview = useMemo(
    () => getTemplatePreview(selectedTemplate),
    [getTemplatePreview, selectedTemplate]
  );
  const editorPreview = useMemo(
    () => getTemplatePreview(form),
    [form, getTemplatePreview]
  );

  const openEditorForTemplate = useCallback((template) => {
    if (!template) return;
    setEditing(template);
    setSelectedTemplateId(template.id);
    setForm({
      name: template?.name || "",
      subject: template?.subject || "",
      preheader: template?.preheader || "",
      body_html: template?.body_html || "",
      body_text: template?.body_text || "",
      cta_label: template?.cta_label || "",
      cta_url: template?.cta_url || "",
      is_active: template?.is_active !== false,
    });
    setTextFallbackEdited(false);
    setEditorOpen(true);
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...DEFAULT_FORM,
      name: goalConfig.createTemplateName,
    });
    setTextFallbackEdited(false);
    setEditorOpen(true);
    setError("");
  };

  const loadTemplates = useCallback(
    async ({ ensureDefaultIfEmpty = false, openDefaultEditorIfCreated = false } = {}) => {
      if (!salonId) return;
      setLoading(true);
      setError("");
      try {
        let rows = await listMarketingTemplatesByGoal(
          salonId,
          selectedGoal,
          token
        );
        rows = Array.isArray(rows) ? rows : [];

        let createdDefault = false;
        if (rows.length === 0 && ensureDefaultIfEmpty) {
          await ensureDefaultEmailReminderTemplate(salonId, selectedGoal, token);
          const reloaded = await listMarketingTemplatesByGoal(
            salonId,
            selectedGoal,
            token
          );
          rows = Array.isArray(reloaded) ? reloaded : [];
          createdDefault = rows.length > 0;
        }

        setTemplates(rows);
        if (rows.length > 0) {
          setSelectedTemplateId((prev) => {
            if (prev && rows.some((row) => String(row.id) === String(prev))) {
              return prev;
            }
            return rows[0].id;
          });
        } else {
          setSelectedTemplateId(null);
        }

        if (createdDefault && openDefaultEditorIfCreated) {
          const defaultTemplate =
            rows.find((row) => row.name === goalConfig.defaultTemplateName) || rows[0];
          if (defaultTemplate) {
            openEditorForTemplate(defaultTemplate);
          }
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "Unable to load reminder templates.");
      } finally {
        setLoading(false);
      }
    },
    [goalConfig.defaultTemplateName, openEditorForTemplate, salonId, selectedGoal, token]
  );

  useEffect(() => {
    loadTemplates({
      ensureDefaultIfEmpty: true,
      openDefaultEditorIfCreated: true,
    });
  }, [loadTemplates]);

  const buildPayload = () => ({
    name: (form.name || "").trim(),
    goal: selectedGoal,
    subject: (form.subject || "").trim(),
    preheader: emptyToNull(form.preheader),
    body_html: (form.body_html || "").trim(),
    body_text: emptyToNull(form.body_text),
    cta_label: emptyToNull(form.cta_label),
    cta_url: emptyToNull(form.cta_url),
    include_promo_code: false,
    include_share_snippet: false,
  });

  const updateBodyHtml = useCallback(
    (nextHtml) => {
      const cleanHtml = sanitizeTemplateEditorHtml(nextHtml);
      setForm((prev) => ({
        ...prev,
        body_html: cleanHtml,
        body_text: textFallbackEdited ? prev.body_text : templateHtmlToText(cleanHtml),
      }));
    },
    [textFallbackEdited]
  );

  const handleSave = async () => {
    if (!salonId) return;
    const payload = buildPayload();
    if (!payload.name || !payload.subject || !templateHtmlToText(payload.body_html)) {
      setError("Template name, subject, and message are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editing?.id) {
        await updateMarketingTemplate(
          editing.id,
          {
            ...payload,
            is_active: !!form.is_active,
          },
          token
        );
      } else {
        const created = await createMarketingTemplate(salonId, payload, token);
        if (created?.id && form.is_active === false) {
          await updateMarketingTemplate(created.id, { is_active: false }, token);
        }
      }
      setEditorOpen(false);
      setEditing(null);
      setForm(DEFAULT_FORM);
      setTextFallbackEdited(false);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save reminder template.");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (template) => {
    if (!salonId || !template?.id) return;
    setError("");
    try {
      await duplicateMarketingTemplate(salonId, template.id, token);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to duplicate template.");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setSaving(true);
    setError("");
    try {
      await deleteMarketingTemplate(deleteTarget.id, token);
      setDeleteTarget(null);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to delete template.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="marketingSectionShell">
      <div className="marketingSectionWide marketing-section-wide marketing-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 pr-20 flex items-center justify-between relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Email Reminders
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="mr-8 rounded-full bg-white px-4 py-2 text-xs md:text-sm font-semibold text-rose-600 hover:bg-rose-50"
            >
              New template
            </button>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div id="overview" className={getSectionClassName("overview")}>
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_email_reminders"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <section
              id="template-setup"
              className={`rounded-2xl border border-slate-200 bg-white p-4 space-y-4 ${getSectionClassName(
                "template-setup"
              )}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base md:text-lg font-semibold text-slate-900">
                    Template Setup
                  </h2>
                  <p className="text-xs text-slate-500">
                    Salon: {salonName || "Unknown"} (ID {salonId || "--"})
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-xs font-semibold text-slate-500">
                    Template type
                  </label>
                  <select
                    value={selectedGoal}
                    onChange={(event) => {
                      setSelectedGoal(event.target.value || REFILL_REMINDER_GOAL);
                      setSelectedTemplateId(null);
                      setEditing(null);
                      setEditorOpen(false);
                      setForm(DEFAULT_FORM);
                      setTextFallbackEdited(false);
                    }}
                    className="h-9 min-w-[180px] rounded-xl border border-slate-300 px-3 text-sm"
                  >
                    {Object.entries(TEMPLATE_GOAL_CONFIG).map(([goal, config]) => (
                      <option key={goal} value={goal}>
                        {config.label}
                      </option>
                    ))}
                  </select>
                  <label className="text-xs font-semibold text-slate-500">
                    Selected template
                  </label>
                  <select
                    value={selectedTemplate?.id || ""}
                    onChange={(event) => setSelectedTemplateId(event.target.value || null)}
                    className="h-9 min-w-[220px] rounded-xl border border-slate-300 px-3 text-sm"
                  >
                    {(sortedTemplates || []).map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    disabled={!selectedTemplate}
                    onClick={() => openEditorForTemplate(selectedTemplate)}
                  >
                    Edit selected
                  </button>
                </div>
              </div>

              {loading && (
                <div className="text-sm text-slate-500">Loading templates...</div>
              )}

              {!loading && !sortedTemplates.length && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                  No {goalConfig.label.toLowerCase()} templates yet.
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {sortedTemplates.map((template) => {
                  const isSelected = String(template.id) === String(selectedTemplate?.id || "");
                  const templatePreview = getTemplatePreview(template);
                  return (
                    <div
                      key={template.id}
                      className={`rounded-2xl border bg-white p-4 space-y-3 ${
                        isSelected
                          ? "border-rose-300 ring-2 ring-rose-200"
                          : "border-slate-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="text-sm font-semibold text-slate-900">
                            {template.name}
                          </div>
                          <div className="text-xs text-slate-600">
                            Subject: {template.subject || "--"}
                          </div>
                          <div className="text-xs text-slate-500">
                            Preview: {templatePreview.subjectPreview || "--"}
                          </div>
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                              template.is_active
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-slate-200 text-slate-600"
                            }`}
                          >
                            {template.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setSelectedTemplateId(template.id)}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Select
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditorForTemplate(template)}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDuplicate(template)}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Duplicate
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(template)}
                            className="rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <SafeHtml
                        className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
                        html={templatePreview.htmlPreview || ""}
                        disableLinks
                      />
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Email Preview</h3>
                <span className="text-xs text-slate-500">
                  {goalConfig.sampleLabel}: {previewContext.first_name}, {previewContext.salon_name}
                </span>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <div className="text-xs text-slate-500">
                  Subject
                </div>
                <div className="text-sm font-semibold text-slate-900">
                  {selectedTemplatePreview.subjectPreview || "--"}
                </div>
                {selectedTemplatePreview.preheaderPreview && (
                  <>
                    <div className="text-xs text-slate-500">
                      Preheader
                    </div>
                    <div className="text-xs text-slate-700">
                      {selectedTemplatePreview.preheaderPreview}
                    </div>
                  </>
                )}
                <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
                  <SafeHtml
                    html={selectedTemplatePreview.htmlPreview || ""}
                    disableLinks
                  />
                </div>
                {selectedTemplatePreview.textPreview && (
                  <pre className="overflow-x-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
                    {selectedTemplatePreview.textPreview}
                  </pre>
                )}
              </div>
            </section>

            <section
              id="variables"
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2 ${getSectionClassName(
                "variables"
              )}`}
            >
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                Available variables
                <InfoTooltip
                  label="Variables"
                  text="Variables are replaced automatically when sending."
                />
              </div>
              <div className="text-xs text-slate-600">
                {goalConfig.variables.join(", ")}
              </div>
            </section>

            <section
              id="client-settings"
              className={`rounded-2xl border border-slate-200 bg-white p-4 space-y-2 ${getSectionClassName(
                "client-settings"
              )}`}
            >
              <h3 className="text-sm font-semibold text-slate-900">Client Settings</h3>
              <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                {goalConfig.clientSettings.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>

            <section
              id="troubleshooting"
              className={`rounded-2xl border border-slate-200 bg-white p-4 space-y-2 ${getSectionClassName(
                "troubleshooting"
              )}`}
            >
              <h3 className="text-sm font-semibold text-slate-900">Troubleshooting</h3>
              <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                {goalConfig.troubleshooting.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      </div>

      <SBINModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editing ? `Edit ${goalConfig.label} template` : `Create ${goalConfig.label} template`}
        size="wide"
        fitContent
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex flex-col gap-1">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                Template Name
                <InfoTooltip text="Internal name for your team. Clients won't see this." />
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, name: event.target.value }))
                }
                className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                Subject
                <InfoTooltip text="This is what clients see in their inbox. Keep it short and clear." />
              </label>
              <input
                type="text"
                value={form.subject}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, subject: event.target.value }))
                }
                className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                Preheader (optional)
                <InfoTooltip text="Preview text shown after the subject in many email apps." />
              </label>
              <input
                type="text"
                value={form.preheader}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, preheader: event.target.value }))
                }
                className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                Message
                <InfoTooltip text={goalConfig.bodyHtmlTooltip} />
              </label>
              <VisualTemplateBodyEditor
                value={form.body_html}
                onChange={updateBodyHtml}
                variables={goalConfig.variables}
              />
            </div>
            <details className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <summary className="cursor-pointer text-xs font-semibold text-slate-600">
                Advanced HTML
              </summary>
              <textarea
                rows={6}
                value={form.body_html}
                onChange={(event) => updateBodyHtml(event.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-mono"
              />
            </details>
            <details className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <summary className="cursor-pointer text-xs font-semibold text-slate-600">
                Plain-text fallback
              </summary>
              <textarea
                rows={5}
                value={form.body_text}
                onChange={(event) => {
                  setTextFallbackEdited(true);
                  setForm((prev) => ({ ...prev, body_text: event.target.value }));
                }}
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
              />
              <button
                type="button"
                className="mt-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                onClick={() => {
                  setTextFallbackEdited(false);
                  setForm((prev) => ({
                    ...prev,
                    body_text: templateHtmlToText(prev.body_html),
                  }));
                }}
              >
                Refresh from message
              </button>
            </details>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-slate-500">CTA Label (optional)</label>
              <input
                type="text"
                value={form.cta_label}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, cta_label: event.target.value }))
                }
                className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-slate-500">CTA URL (optional)</label>
              <input
                type="text"
                value={form.cta_url}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, cta_url: event.target.value }))
                }
                className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
              />
            </div>
            <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border border-slate-300 text-rose-600 focus:ring-0"
                checked={!!form.is_active}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, is_active: event.target.checked }))
                }
              />
              Active template
              <InfoTooltip text="If off, this template won't be used for reminders." />
            </label>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Preview
              </div>
              <div className="mt-3 text-sm text-slate-700">
                Subject: {editorPreview.subjectPreview || "--"}
              </div>
              {editorPreview.preheaderPreview && (
                <div className="mt-2 text-xs text-slate-500">
                  Preheader: {editorPreview.preheaderPreview}
                </div>
              )}
              <SafeHtml
                className="mt-3 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600"
                html={editorPreview.htmlPreview || ""}
                disableLinks
              />
              {editorPreview.textPreview && (
                <pre className="mt-3 overflow-x-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
                  {editorPreview.textPreview}
                </pre>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => setEditorOpen(false)}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Template"}
          </button>
        </div>
      </SBINModal>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title={`Delete ${goalConfig.label} template`}
        message={`Delete "${deleteTarget?.name || "this template"}"?`}
        confirmText={saving ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
